import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAlert } from '../context/AlertContext';
import { MAIN_DOMAIN } from '../config';
import { Calendar, Users, Clock, CheckCircle, Eye, EyeOff, ArrowLeft, ArrowRight } from 'lucide-react';
import { StepIndicator } from '../components/ui/StepIndicator';
import './RegisterTenant.css';

const RegisterTenant = () => {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        company_name: '',
        subdomain: '',
        admin_username: '',
        admin_email: '',
        admin_password: '',
    });
    const [showPassword, setShowPassword] = useState(false);
    const { showAlert } = useAlert();
    const navigate = useNavigate();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleNext = () => setStep(step + 1);
    const handlePrev = () => setStep(step - 1);

    const handleStepClick = (targetStep: number) => {
        if (targetStep < step) {
            setStep(targetStep);
            return;
        }
        if (targetStep === 2 && formData.company_name && formData.subdomain) {
            setStep(2);
        } else if (targetStep === 3 &&
            formData.company_name &&
            formData.subdomain &&
            formData.admin_username &&
            formData.admin_email &&
            formData.admin_password.length >= 8) {
            setStep(3);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        console.log('🚀 Iniciando registro de tenant...');
        console.log('📝 Dados do formulário:', formData);

        setLoading(true);
        try {
            console.log('📡 Enviando requisição para /register-tenant/');
            const response = await api.post('/register-tenant/', formData);
            console.log('✅ Resposta recebida:', response.data);

            showAlert('Sua agenda foi criada com sucesso! Você será redirecionado.', { type: 'success' });

            setTimeout(() => {
                const protocol = window.location.protocol;
                const isProduction = MAIN_DOMAIN !== 'localhost';
                let loginUrl: string;

                if (isProduction) {
                    // Production: subdomain.agenda.ypecode.com.br/login (no port)
                    loginUrl = `${protocol}//${formData.subdomain}.${MAIN_DOMAIN}/login`;
                } else {
                    // Development: subdomain.localhost:3000/login
                    const frontendPort = window.location.port || '3000';
                    loginUrl = `${protocol}//${formData.subdomain}.localhost:${frontendPort}/login`;
                }

                console.log('🔄 Redirecionando para:', loginUrl);
                window.location.href = loginUrl;
            }, 3000);
        } catch (err: any) {
            console.error('❌ Erro ao registrar tenant:', err);
            console.error('📋 Detalhes do erro:', {
                status: err.response?.status,
                statusText: err.response?.statusText,
                data: err.response?.data,
                headers: err.response?.headers
            });

            let errorMsg = 'Erro ao criar empresa. Tente novamente mais tarde.';

            if (err.response?.data) {
                const data = err.response.data;
                // Caso 1: Erro genérico (ex: 409 Conflict ou 500 Internal Error com chave "error")
                if (data.error) {
                    errorMsg = data.error;
                }
                // Caso 2: Erros de validação (ex: 400 Bad Request)
                else {
                    // Pega a primeira mensagem de erro de qualquer campo que falhou
                    const firstField = Object.keys(data)[0];
                    if (firstField && Array.isArray(data[firstField])) {
                        errorMsg = `${firstField}: ${data[firstField][0]}`;
                        // Mapeamento simples de nomes de campos para algo mais amigável, se desejar
                        if (firstField === 'subdomain') errorMsg = `Subdomínio: ${data[firstField][0]}`;
                        if (firstField === 'company_name') errorMsg = `Nome da Empresa: ${data[firstField][0]}`;
                    }
                }
            }

            showAlert(errorMsg, { type: 'error', title: 'Falha no Cadastro' });
            setLoading(false);
        }
    };


    const features = [
        { icon: Calendar, title: 'Agendamento Inteligente', desc: 'Gerencie horários com facilidade' },
        { icon: Users, title: 'Gestão de Clientes', desc: 'Organize sua base de clientes' },
        { icon: Clock, title: 'Disponibilidade 24/7', desc: 'Seus clientes agendam a qualquer hora' },
        { icon: CheckCircle, title: 'Confirmações Automáticas', desc: 'Reduza faltas com lembretes' }
    ];

    return (
        <div className="register-tenant-page">
            <Container fluid className="h-100">
                <Row className="h-100 g-0">
                    {/* Left Side - Hero Section (Hidden on mobile) */}
                    <Col lg={6} className="d-none d-lg-flex hero-section">
                        <div className="hero-watermark">
                            <img
                                src="/agendainicioGold.png"
                                alt="Y-Agenda"
                                className="hero-watermark-image"
                            />
                        </div>
                        <div className="hero-content">
                            <div className="hero-header">
                                <h1 className="hero-title">
                                    Transforme sua gestão de horários
                                </h1>
                                <p className="hero-subtitle">
                                    Crie sua agenda profissional em minutos e comece a receber agendamentos online hoje mesmo.
                                </p>
                            </div>

                            <div className="features-grid">
                                {features.map((feature, index) => (
                                    <div key={index} className="feature-card">
                                        <div className="feature-icon">
                                            <feature.icon size={24} />
                                        </div>
                                        <div className="feature-text">
                                            <h3>{feature.title}</h3>
                                            <p>{feature.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="hero-footer">
                                <p>
                                    <CheckCircle size={16} className="me-2" />
                                    Desenvolvido por Ypecode Tecnologia © {new Date().getFullYear()}
                                </p>
                            </div>
                        </div>
                    </Col>

                    {/* Right Side - Form Section */}
                    <Col lg={6} className="form-section">
                        <div className="form-wrapper">
                            <div className="form-container">
                                {/* Logo Header - Centralizado */}
                                <div className="register-logo-header text-center mb-4">
                                    <h1 className="register-logo-title">Y-Agenda</h1>
                                    <p className="register-logo-subtitle">Gestão inteligente de agendamentos</p>
                                </div>

                                {/* Mobile Header */}
                                <div className="d-lg-none text-center mb-4">
                                    <h2 className="fw-bold mb-2">Crie sua Agenda Online</h2>
                                    <p className="text-muted">Comece a gerenciar seus horários de forma profissional</p>
                                </div>

                                <Card className="glass-card border-0 shadow-lg">
                                    <Card.Body className="p-4 p-md-5">
                                        <StepIndicator
                                            steps={[{}, {}, {}]}
                                            currentStep={step}
                                            onStepClick={handleStepClick}
                                            showProgressBar={true}
                                        />

                                        <Form onSubmit={handleSubmit}>
                                            {step === 1 && (
                                                <div className="step-content animation-fade-in">
                                                    <h4 className="mb-4 fw-bold">Informações da Empresa</h4>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Nome da Empresa</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="company_name"
                                                            placeholder="Ex: Clínica Saúde & Bem Estar"
                                                            value={formData.company_name}
                                                            onChange={handleChange}
                                                            required
                                                            className="form-input"
                                                        />
                                                    </Form.Group>
                                                    <Form.Group className="mb-4">
                                                        <Form.Label>URL Desejada</Form.Label>
                                                        {/* Versão Desktop - com span fixo */}
                                                        <div className="input-group d-none d-lg-flex">
                                                            <Form.Control
                                                                type="text"
                                                                name="subdomain"
                                                                placeholder="minha-empresa"
                                                                value={formData.subdomain}
                                                                onChange={handleChange}
                                                                required
                                                                className="form-input"
                                                            />
                                                            <span className="input-group-text">.agenda.ypecode.com.br</span>
                                                        </div>
                                                        {/* Versão Mobile - sem span fixo */}
                                                        <div className="d-lg-none">
                                                            <Form.Control
                                                                type="text"
                                                                name="subdomain"
                                                                placeholder="minha-empresa"
                                                                value={formData.subdomain}
                                                                onChange={handleChange}
                                                                required
                                                                className="form-input"
                                                            />
                                                        </div>
                                                        <Form.Text className="text-muted">
                                                            Sua agenda ficará em: <strong>{formData.subdomain || '...'}.agenda.ypecode.com.br</strong>
                                                        </Form.Text>
                                                    </Form.Group>
                                                    <div className="d-grid">
                                                        <Button
                                                            variant="primary"
                                                            size="lg"
                                                            onClick={handleNext}
                                                            disabled={!formData.company_name || !formData.subdomain}
                                                            className="btn-next d-flex align-items-center justify-content-center gap-2"
                                                        >
                                                            Próximo Passo <ArrowRight size={20} />
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}

                                            {step === 2 && (
                                                <div className="step-content animation-fade-in">
                                                    <h4 className="mb-4 fw-bold">Dados do Administrador</h4>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Usuário</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="admin_username"
                                                            placeholder="Ex: admin"
                                                            value={formData.admin_username}
                                                            onChange={handleChange}
                                                            required
                                                            className="form-input"
                                                        />
                                                    </Form.Group>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>E-mail Profissional</Form.Label>
                                                        <Form.Control
                                                            type="email"
                                                            name="admin_email"
                                                            placeholder="contato@empresa.com"
                                                            value={formData.admin_email}
                                                            onChange={handleChange}
                                                            required
                                                            className="form-input"
                                                        />
                                                    </Form.Group>
                                                    <Form.Group className="mb-4">
                                                        <Form.Label>Senha de Acesso</Form.Label>
                                                        <div className="input-group input-group-password">
                                                            <Form.Control
                                                                type={showPassword ? "text" : "password"}
                                                                name="admin_password"
                                                                placeholder="Mínimo 8 caracteres"
                                                                value={formData.admin_password}
                                                                onChange={handleChange}
                                                                required
                                                                minLength={8}
                                                                className="form-input"
                                                            />
                                                            <Button
                                                                variant="outline-secondary"
                                                                onClick={() => setShowPassword(!showPassword)}
                                                                tabIndex={-1}
                                                                className="btn-toggle-password"
                                                            >
                                                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                                            </Button>
                                                        </div>
                                                    </Form.Group>
                                                    <div className="step-actions-register">
                                                        <Button
                                                            variant="primary"
                                                            size="lg"
                                                            onClick={handleNext}
                                                            disabled={!formData.admin_username || !formData.admin_email || formData.admin_password.length < 8}
                                                            className="btn-next d-flex align-items-center justify-content-center gap-2"
                                                        >
                                                            Revisar <ArrowRight size={20} />
                                                        </Button>
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="lg"
                                                            onClick={handlePrev}
                                                            className="btn-back d-flex align-items-center justify-content-center gap-2"
                                                        >
                                                            <ArrowLeft size={20} /> Voltar
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}

                                            {step === 3 && (
                                                <div className="step-content animation-fade-in">
                                                    <h4 className="mb-4 fw-bold">Pronto para começar?</h4>
                                                    <div className="review-box p-4 rounded mb-4">
                                                        <div className="review-item">
                                                            <span className="review-label">Empresa:</span>
                                                            <span className="review-value">{formData.company_name}</span>
                                                        </div>
                                                        <div className="review-item">
                                                            <span className="review-label">URL:</span>
                                                            <span className="review-value">{formData.subdomain}.agenda.ypecode.com.br</span>
                                                        </div>
                                                        <div className="review-item">
                                                            <span className="review-label">Admin:</span>
                                                            <span className="review-value">{formData.admin_email}</span>
                                                        </div>
                                                    </div>
                                                    <p className="text-center text-muted small mb-4">
                                                        Ao clicar em criar, prepararemos seu espaço exclusivo em nossos servidores.
                                                        Isso pode levar alguns segundos.
                                                    </p>
                                                    <div className="step-actions-register">
                                                        <Button
                                                            variant="success"
                                                            size="lg"
                                                            type="submit"
                                                            disabled={loading}
                                                            className="btn-create d-flex align-items-center justify-content-center gap-2"
                                                        >
                                                            {loading ? (
                                                                <>
                                                                    <span className="spinner-border spinner-border-sm me-2" />
                                                                    Criando...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <CheckCircle size={20} />
                                                                    Criar Minha Agenda
                                                                </>
                                                            )}
                                                        </Button>
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="lg"
                                                            onClick={handlePrev}
                                                            disabled={loading}
                                                            className="btn-back d-flex align-items-center justify-content-center gap-2"
                                                        >
                                                            <ArrowLeft size={20} /> Alterar
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                        </Form>
                                    </Card.Body>
                                </Card>
                            </div>
                        </div>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default RegisterTenant;
